import { createAction } from '@reduxjs/toolkit';
import { useCallback, useMemo, useState } from 'react';

import { useActionObservableCallback } from '@perpay-web/hooks/useActionObservableCallback';
import { hookAction$ } from '@perpay-web/services/singletons';
import { createContextProvider } from '@perpay-web/utils/componentUtils';

const DEFAULT_POSITION_BOTTOM = 116;
const DEFAULT_POSITION_BOTTOM_DESKTOP = 64;

export const addToastAction = createAction('ADD_TOAST');

/**
 * @typedef {Object} Toast
 * @property {string} id Unique identifier generated at creation time.
 * @property {string} message Message shown to the user.
 * @property {'success'|'error'|'warning'} [type] Visual state determining color & icon.
 * @property {number} [duration] Optional lifespan in milliseconds before auto-dismiss.
 * @property {number} [positionBottom] Optional bottom position offset for mobile screen sizes.
 * @property {number} [positionBottomDesktop] Optional bottom position offset for desktop screen sizes.
 */

/** @typedef {Omit<Toast, 'id'>} AddToastOptions */

const useToastProviderHook = () => {
    /** @type {[Toast[], React.Dispatch<React.SetStateAction<Toast[]>>]} */
    const [toasts, setToasts] = useState([]);
    const [offsetBottom, setOffsetBottom] = useState(DEFAULT_POSITION_BOTTOM);
    const [offsetBottomDesktop, setOffsetBottomDesktop] = useState(
        DEFAULT_POSITION_BOTTOM_DESKTOP,
    );

    /** @type {(options: AddToastOptions) => void} */
    const addToast = useCallback(
        ({
            message,
            type = 'success',
            duration,
            positionBottom,
            positionBottomDesktop,
        }) => {
            if (!message) {
                return;
            }
            const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
            // Ensure `type` is treated as the Toast union type.
            /** @type {Toast} */
            const newToast = {
                id,
                message,
                type,
                // default 500ms per word (Material UI recommendation)
                // Minimum 3 seconds
                duration:
                    duration || Math.max(message.split(' ').length * 500, 3000),
            };
            setToasts((prev) => [...prev, newToast]);
            setOffsetBottom(positionBottom || DEFAULT_POSITION_BOTTOM);
            setOffsetBottomDesktop(
                positionBottomDesktop || DEFAULT_POSITION_BOTTOM_DESKTOP,
            );
        },
        [],
    );

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    useActionObservableCallback(hookAction$, addToastAction().type, (payload) =>
        addToast({
            message: payload.message,
            type: payload.type,
            duration: payload.duration,
            positionBottom: payload.positionBottom,
            positionBottomDesktop: payload.positionBottomDesktop,
        }),
    );

    return {
        toasts,
        addToast,
        removeToast,
        positionBottom: offsetBottom,
        positionBottomDesktop: offsetBottomDesktop,
    };
};

const providerFactory = (Context) => {
    const Provider = ({ children }) => {
        const {
            toasts,
            addToast,
            removeToast,
            positionBottom,
            positionBottomDesktop,
        } = useToastProviderHook();

        // Provide full state so consumers can pick what they need.
        const contextValue = useMemo(
            () => ({
                toasts,
                addToast,
                removeToast,
                positionBottom,
                positionBottomDesktop,
            }),
            [
                toasts,
                addToast,
                removeToast,
                positionBottom,
                positionBottomDesktop,
            ],
        );

        return (
            <Context.Provider value={contextValue}>{children}</Context.Provider>
        );
    };

    return Provider;
};

/**
 * Shape of the value that ends up in React context.
 * (You already have Toast and AddToastOptions above, reuse them here.)
 * @typedef {Object} ToastContextValue
 * @property {Toast[]} toasts
 * @property {(options: AddToastOptions) => void} addToast
 * @property {(id: string) => void} removeToast
 * @property {number} positionBottom
 * @property {number} positionBottomDesktop
 */

export const [ToastProvider, useToastContext] =
    /** @type {[any, () => ToastContextValue]} */ (
        createContextProvider(providerFactory)
    );
